import React, { useContext, useEffect, useState } from 'react';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';

import {
  getFreightRules,
  getFreightRuleScopes,
  saveFreightRule,
  deleteFreightRule,
} from '../../../Actions';
import { AlertContext } from '../../../Context/AlertContextProvider';
import { formatDttm } from '../../../Util/timezone';
import { chargeRateText } from '../../../Util/freightCharges';

// Freight & Tariff Rates on the Cost Adjustments page: scoped rules
// (freight_tariff_rule table) describing what a vendor charges for freight or
// tariffs — a % of cost (fuel surcharge), a flat per-order fee, a per-unit
// rate, and/or a min/max cap. Display-only: charges show on the product page
// and search results for staff but are NOT part of the price calculation.
// Unlike cost adjustment rules, every matching rule applies (charges stack).

const EMPTY_RULE = {
  freight_tariff_rule_id: null,
  charge_name: '',
  charge_type_cd: 'FREIGHT',
  manufacturer_uuid: '',
  distributor_uuid: '',
  material_class_cd: '',
  material_sub_class_cd: '',
  percent_of_cost: '',
  flat_amount: '',
  per_unit_amount: '',
  per_unit_uom_type_cd: '',
  min_charge: '',
  max_charge: '',
  notes: '',
  effective_start: '',
  effective_end: '',
};

// mysql DATE/DATETIME string -> the yyyy-mm-dd an <input type="date"> wants.
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const formatNumber = (value) =>
  value === null || value === undefined || value === '' ? null : parseFloat(Number(value).toFixed(4));

const FreightRates = () => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [rules, setRules] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  const loadRules = () => {
    setLoading(true);
    Promise.all([getFreightRules(), scopes ? Promise.resolve(null) : getFreightRuleScopes()])
      .then(([rulesRes, scopesRes]) => {
        setRules(rulesRes.data?.rules || []);
        if (scopesRes) setScopes(scopesRes.data);
      })
      .catch(() => {
        setMessage('Error loading freight rates');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadRules, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditor = (rule) => {
    if (rule) {
      setDraft({
        freight_tariff_rule_id: rule.freight_tariff_rule_id,
        charge_name: rule.charge_name || '',
        charge_type_cd: rule.charge_type_cd || 'FREIGHT',
        manufacturer_uuid: rule.manufacturer_uuid || '',
        distributor_uuid: rule.distributor_uuid || '',
        material_class_cd: rule.material_class_cd || '',
        material_sub_class_cd: rule.material_sub_class_cd || '',
        percent_of_cost: formatNumber(rule.percent_of_cost) ?? '',
        flat_amount: formatNumber(rule.flat_amount) ?? '',
        per_unit_amount: formatNumber(rule.per_unit_amount) ?? '',
        per_unit_uom_type_cd: rule.per_unit_uom_type_cd || '',
        min_charge: formatNumber(rule.min_charge) ?? '',
        max_charge: formatNumber(rule.max_charge) ?? '',
        notes: rule.notes || '',
        effective_start: toDateInput(rule.effective_start),
        effective_end: toDateInput(rule.effective_end),
      });
    } else {
      setDraft(EMPTY_RULE);
    }
    setEditorOpen(true);
  };

  const setField = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  const hasValue =
    draft.percent_of_cost !== '' || draft.flat_amount !== '' || draft.per_unit_amount !== '';

  const handleSave = () => {
    setSaving(true);
    saveFreightRule(draft)
      .then(() => {
        setEditorOpen(false);
        setMessage('Freight rate saved');
        setMessageType('success');
        handleAlertOpen();
        loadRules();
      })
      .catch((err) => {
        setMessage(err.response?.data?.message || 'Error saving freight rate');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (rule) => {
    if (!window.confirm('Delete this freight rate?')) {
      return;
    }
    deleteFreightRule(rule.freight_tariff_rule_id)
      .then(() => {
        setMessage('Freight rate deleted');
        setMessageType('success');
        handleAlertOpen();
        loadRules();
      })
      .catch(() => {
        setMessage('Error deleting freight rate');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const scopeText = (rule) => {
    const parts = [];
    if (rule.manufacturer_name) parts.push(rule.manufacturer_name);
    if (rule.distributor_name) parts.push(rule.distributor_name);
    if (rule.material_class) parts.push(rule.material_class);
    if (rule.material_sub_class) parts.push(rule.material_sub_class);
    return parts.length ? parts.join(' · ') : 'All products';
  };

  const scopeSelect = (label, field, options, valueKey, labelKey) => (
    <FormControl style={{ minWidth: 170, flex: 1 }}>
      <InputLabel shrink>{label}</InputLabel>
      <NativeSelect value={draft[field]} onChange={setField(field)}>
        <option value="">All</option>
        {(options || []).map((o) => (
          <option key={o[valueKey]} value={o[valueKey]}>
            {o[labelKey]}
          </option>
        ))}
      </NativeSelect>
    </FormControl>
  );

  // Sub-classes narrow to the chosen material class.
  const subClassOptions = (scopes?.material_sub_classes || []).filter(
    (sc) => !draft.material_class_cd || sc.material_class_cd === draft.material_class_cd
  );

  return (
    <Accordion id="tour-freight-rates" elevation={5} style={{ margin: '0 10px 20px' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id="tour-freight-rates-header">
        <div>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Freight &amp; Tariff Rates
          </Typography>
          <Typography variant="body2" color="textSecondary">
            What each vendor charges for freight and tariffs — fuel surcharge %, flat delivery
            fees, per-unit rates. Shown to staff on products (and to customers with the
            Settings &gt; Show Freight &amp; Tariffs toggle); not part of the price calculation.
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', paddingTop: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Typography variant="body2" color="textSecondary">
            A field left as "All" matches everything. Every rate that matches a product is shown
            together — charges stack (e.g. a fuel surcharge on top of a delivery fee).
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openEditor(null)}
            disabled={!scopes}
          >
            Add Rate
          </Button>
        </div>

        {loading && !rules ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </div>
        ) : null}

        {rules && !rules.length ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: '16px 0' }}>
            No freight rates yet. Add what your vendors charge to see freight and tariff costs
            on products.
          </Typography>
        ) : null}

        {rules && rules.length ? (
          <TableContainer style={{ marginTop: 12, maxHeight: '50vh' }}>
            <Table size="small" stickyHeader aria-label="freight and tariff rates">
              <TableHead>
                <TableRow>
                  {['Applies To', 'Charge', 'Type', 'Rate', 'Effective', 'Last Modified', ''].map((h) => (
                    <TableCell key={h} style={{ backgroundColor: '#fafafa', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.freight_tariff_rule_id} hover>
                    <TableCell>{scopeText(rule)}</TableCell>
                    <TableCell>
                      {rule.charge_name}
                      {rule.notes ? (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {rule.notes}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{rule.charge_type_cd === 'TARIFF' ? 'Tariff' : 'Freight'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{chargeRateText(rule)}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {toDateInput(rule.effective_start) || 'Always'} → {toDateInput(rule.effective_end) || 'No end'}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {rule.last_modified_dttm ? formatDttm(rule.last_modified_dttm) : '—'}
                      {rule.last_updated_by_name ? (
                        <Typography variant="caption" display="block" color="textSecondary">
                          by {rule.last_updated_by_name}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                      <Button size="small" onClick={() => openEditor(rule)}>
                        Edit
                      </Button>
                      <Button size="small" style={{ color: '#f44336' }} onClick={() => handleDelete(rule)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </AccordionDetails>

      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {draft.freight_tariff_rule_id ? 'Edit Freight Rate' : 'New Freight Rate'}
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <TextField
              label="Charge Name"
              value={draft.charge_name}
              onChange={setField('charge_name')}
              placeholder="e.g. FSC, Tile Delivery, Carpet Freight"
              InputLabelProps={{ shrink: true }}
              style={{ flex: 2, minWidth: 220 }}
            />
            <FormControl style={{ minWidth: 150, flex: 1 }}>
              <InputLabel shrink>Type</InputLabel>
              <NativeSelect value={draft.charge_type_cd} onChange={setField('charge_type_cd')}>
                <option value="FREIGHT">Freight</option>
                <option value="TARIFF">Tariff</option>
              </NativeSelect>
            </FormControl>
          </div>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            Who it applies to — every field left as "All" is a wildcard.
          </Typography>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            {scopeSelect('Manufacturer', 'manufacturer_uuid', scopes?.manufacturers, 'manufacturer_uuid', 'manufacturer_name')}
            {scopeSelect('Distributor', 'distributor_uuid', scopes?.distributors, 'distributor_uuid', 'distributor_name')}
            {scopeSelect('Material Class', 'material_class_cd', scopes?.material_classes, 'material_class_cd', 'material_class')}
            {scopeSelect('Sub-class', 'material_sub_class_cd', subClassOptions, 'material_sub_class_cd', 'material_sub_class')}
          </div>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            What the vendor charges — set at least one. They combine, so "$50 plus 38% FSC,
            min $65" is a flat charge + a % of cost with a minimum. Enter 0 for an explicit
            "No Charge".
          </Typography>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <TextField
              label="% of Cost"
              type="number"
              value={draft.percent_of_cost}
              onChange={setField('percent_of_cost')}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              style={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              label="Flat Charge"
              type="number"
              value={draft.flat_amount}
              onChange={setField('flat_amount')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              style={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              label="Per Unit"
              type="number"
              value={draft.per_unit_amount}
              onChange={setField('per_unit_amount')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              style={{ flex: 1, minWidth: 130 }}
            />
            <FormControl style={{ minWidth: 130, flex: 1 }}>
              <InputLabel shrink>Per Unit UOM</InputLabel>
              <NativeSelect value={draft.per_unit_uom_type_cd} onChange={setField('per_unit_uom_type_cd')}>
                <option value="">—</option>
                {(scopes?.uoms || []).map((u) => (
                  <option key={u.uom_type_cd} value={u.uom_type_cd}>
                    {u.uom_type_cd} — {u.uom_type}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <TextField
              label="Minimum Charge"
              type="number"
              value={draft.min_charge}
              onChange={setField('min_charge')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText='e.g. "plus 38% FSC, min $65"'
              style={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label="Maximum Charge (cap)"
              type="number"
              value={draft.max_charge}
              onChange={setField('max_charge')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText='e.g. "6%, capped at $60"'
              style={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label="Notes"
              value={draft.notes}
              onChange={setField('notes')}
              placeholder='e.g. "under $500 order is $200"'
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 255 }}
              style={{ flex: 2, minWidth: 220 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <TextField
              label="Effective From"
              type="date"
              value={draft.effective_start}
              onChange={setField('effective_start')}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = starts immediately"
              style={{ flex: 1, minWidth: 170 }}
            />
            <TextField
              label="Effective Until"
              type="date"
              value={draft.effective_end}
              onChange={setField('effective_end')}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = runs until removed"
              style={{ flex: 1, minWidth: 170 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !hasValue || !draft.charge_name.trim()}
          >
            {saving ? 'Saving…' : 'Save Rate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default FreightRates;
